import { Dispatch, SetStateAction, useState } from 'react';

import { FiltersState, SearchFilters } from '../filters.types';
import {
  useFiltersFromStorage,
  GetStoredFiltersByType,
} from './useFiltersFromStorage';
import { useSearchQueryParams } from '../../hooks';
import { SearchPartnerParams } from '@keaze/web/common/interfaces';
import { DEFAULT_FILTERS_STATE } from '../filters';
import { OccupancyType } from '@keaze/web/common/enums';

type ExpectedUseStateFilters = {
  filters: FiltersState;
  setFilters: Dispatch<SetStateAction<FiltersState>>;
  updateStoredFilters: (filters: FiltersState) => void;
  getStoredFiltersByType: GetStoredFiltersByType;
};

type UseStateFilters = (
  withOnlyLocationData?: boolean
) => ExpectedUseStateFilters;

type GenerateInitialFiltersArgs = {
  storedFilters: FiltersState;
  partnerParams: SearchPartnerParams;
  withOnlyLocationData: boolean;
};

type GenerateInitialFilters = (
  args: GenerateInitialFiltersArgs
) => FiltersState;

const generateInitialFilters: GenerateInitialFilters = ({
  storedFilters,
  partnerParams,
  withOnlyLocationData,
}) => {
  if (withOnlyLocationData) {
    return {
      ...DEFAULT_FILTERS_STATE,
      [SearchFilters.OccupancyType]: OccupancyType.Sale,
      [SearchFilters.Location]: storedFilters.location,
    };
  }

  if (partnerParams.tenures) {
    const schemes = [
      ...(storedFilters[SearchFilters.Schemes] as number[]),
      partnerParams.tenures,
    ];

    return {
      ...storedFilters,
      [SearchFilters.Schemes]: [...new Set(schemes)],
    };
  }

  return storedFilters;
};

export const useStateFilters: UseStateFilters = (
  withOnlyLocationData = false
) => {
  const { partnerParams } = useSearchQueryParams();
  const {
    storedFiltersByType,
    updateStoredFilters,
    getStoredFiltersByType,
  } = useFiltersFromStorage();
  const [filters, setFilters] = useState<FiltersState>(
    generateInitialFilters({
      storedFilters: storedFiltersByType,
      partnerParams,
      withOnlyLocationData,
    })
  );

  return {
    filters,
    setFilters,
    updateStoredFilters,
    getStoredFiltersByType,
  };
};
